import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Chip,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon
} from '@mui/icons-material';
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import ListTool from '@editorjs/list';
import Paragraph from '@editorjs/paragraph';
import SimpleImage from '@editorjs/simple-image';
import { fetchNotes, createNote, updateNote, deleteNote } from '../../features/notes/notesSlice';

const TechniqueNotes = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { items: notes, loading, error } = useSelector(state => state.notes);
  const { user } = useSelector(state => state.auth);
  const { classes } = useSelector(state => state.techniques);
  const editorRef = useRef(null);
  const editorInstanceRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const [localContent, setLocalContent] = useState(null);
  const [openEditor, setOpenEditor] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [showAllNotes, setShowAllNotes] = useState(false);

  const currentClass = classes?.find(c => c.id === parseInt(classId));
  const currentNotes = notes[classId] || [];

  useEffect(() => {
    if (classId) {
      dispatch(fetchNotes(classId));
    }
  }, [dispatch, classId]);

  const initEditor = () => {
    const content = editingNote ? editingNote.content : newNoteContent;
    
    if (editorInstanceRef.current) {
      editorInstanceRef.current.destroy();
    }
    
    editorInstanceRef.current = new EditorJS({
      holder: 'editorjs',
      placeholder: editingNote ? 'Edit your note...' : 'Write a new note...',
      tools: {
        header: {
          class: Header,
          config: {
            placeholder: 'Enter a header',
            levels: [2, 3],
            defaultLevel: 2
          }
        },
        list: {
          class: ListTool,
          inlineToolbar: true
        },
        paragraph: {
          class: Paragraph,
          inlineToolbar: true
        },
        image: {
          class: SimpleImage,
          inlineToolbar: true
        }
      },
      data: content ? JSON.parse(content) : {},
      onChange: () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        
        debounceTimerRef.current = setTimeout(async () => {
          try {
            if (!editorInstanceRef.current) return;
            
            const savedContent = await editorInstanceRef.current.save();
            const contentString = JSON.stringify(savedContent);
            
            if (editingNote && contentString !== editingNote.content) {
              setEditingNote({...editingNote, content: contentString});
            } else if (!editingNote && contentString !== newNoteContent) {
              setNewNoteContent(contentString);
            }
          } catch (err) {
            console.error('Failed to save editor content:', err);
          }
        }, 500);
      }
    });
    
    editorRef.current = editorInstanceRef.current;
  };

  useEffect(() => {
    if (openEditor) {
      setTimeout(() => {
        initEditor();
      }, 0);
    }
    
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      
      if (editorInstanceRef.current) {
        editorInstanceRef.current.destroy();
        editorInstanceRef.current = null;
      }
      
      editorRef.current = null;
    };
  }, [openEditor, editingNote?.id]);

  useEffect(() => {
    if (!localContent && editingNote?.content) {
      setLocalContent(JSON.parse(editingNote.content));
    }
  }, [editingNote, localContent]);

  const handleCreateNote = async () => {
    try {
      await dispatch(createNote({
        content: newNoteContent,
        classId: parseInt(classId)
      })).unwrap();
      setOpenEditor(false);
      setNewNoteContent('');
      dispatch(fetchNotes(classId));
    } catch (error) {
      console.error('Failed to create note:', error);
    }
  };

  const handleUpdateNote = async () => {
    try {
      await dispatch(updateNote({
        noteId: editingNote.id,
        content: editingNote.content,
        classId: parseInt(classId)
      })).unwrap();
      setOpenEditor(false);
      setEditingNote(null);
      dispatch(fetchNotes(classId));
    } catch (error) {
      console.error('Failed to update note:', error);
    }
  };

  const handleDeleteClick = (note) => {
    setNoteToDelete(note);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deleteNote({
        noteId: noteToDelete.id,
        classId: parseInt(classId)
      })).unwrap();
      setDeleteConfirmOpen(false);
      setNoteToDelete(null);
      dispatch(fetchNotes(classId));
    } catch (error) {
      console.error('Failed to delete note:', error);
    }
  };

  const renderContent = (content) => {
    try {
      if (!content) return '';
      const data = typeof content === 'string' ? JSON.parse(content) : content;
      
      return data.blocks.map(block => {
        switch (block.type) {
          case 'paragraph':
            return `<p>${block.data.text}</p>`;
          case 'header':
            return `<h${block.data.level}>${block.data.text}</h${block.data.level}>`;
          case 'list':
            const listItems = block.data.items
              .map(item => {
                if (typeof item === 'object' && item.content) {
                  return `<li>${item.content}</li>`;
                }
                return `<li>${item}</li>`;
              })
              .join('');
            
            const listStyle = block.data.style === 'ordered' ? 'decimal' : 'disc';
            return `
              <${block.data.style === 'ordered' ? 'ol' : 'ul'} style="list-style-type: ${listStyle}; margin-left: 20px;">
                ${listItems}
              </${block.data.style === 'ordered' ? 'ol' : 'ul'}>
            `;
          case 'image':
            return `<img src="${block.data.url}" alt="${block.data.caption || ''}" style="max-width: 100%;" />`;
          default:
            return '';
        }
      }).join('');
    } catch (error) {
      console.error('Error rendering content:', error);
      return content || '';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Header Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/techniques')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" component="h1">
            Notes for Class {currentClass?.number}
          </Typography>
        </Box>

        {/* Controls Section */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2 
        }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenEditor(true)}
          >
            Add Note
          </Button>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={showAllNotes ? "Showing All Notes" : "Showing My Notes"}
              color={showAllNotes ? "primary" : "default"}
              onClick={() => setShowAllNotes(!showAllNotes)}
            />
          </Box>
        </Box>

        {/* Notes List */}
        <Paper sx={{ p: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : currentNotes.length === 0 ? (
            <Alert severity="info">
              No notes yet. Be the first to add one!
            </Alert>
          ) : (
            <List>
              {currentNotes
                .filter(note => showAllNotes || note.username === user?.username)
                .map((note) => (
                  <ListItem
                    key={note.id}
                    sx={{
                      mb: 2,
                      p: 2,
                      bgcolor: note.username === user?.username ? 
                        'action.selected' : 'background.paper',
                      borderRadius: 1,
                      boxShadow: 1
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Avatar sx={{ mr: 2 }}>
                          {note.username[0].toUpperCase()}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle2">
                            {note.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(note.created_at).toLocaleString()}
                          </Typography>
                        </Box>
                        {note.username === user?.username && (
                          <Box>
                            <IconButton onClick={() => {
                              setEditingNote(note);
                              setOpenEditor(true);
                            }}>
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteClick(note)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      <Box
                        dangerouslySetInnerHTML={{ __html: renderContent(note.content) }}
                        sx={{
                          '& img': { maxWidth: '100%' },
                          '& p': { margin: '0.5em 0' },
                          '& h2': { fontSize: '1.3em', margin: '0.5em 0' },
                          '& h3': { fontSize: '1.1em', margin: '0.5em 0' },
                          '& ul, & ol': {
                            margin: '0.5em 0',
                            paddingLeft: '1.5em',
                            listStylePosition: 'outside'
                          },
                          '& li': {
                            margin: '0.25em 0',
                            paddingLeft: '0.5em'
                          }
                        }}
                      />
                    </Box>
                  </ListItem>
                ))}
            </List>
          )}
        </Paper>
      </Box>

      {/* Editor Dialog */}
      <Dialog
        open={openEditor}
        onClose={() => {
          setOpenEditor(false);
          setEditingNote(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingNote ? 'Edit Note' : 'New Note'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <div id="editorjs" style={{ minHeight: '200px' }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenEditor(false);
            setEditingNote(null);
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={editingNote ? handleUpdateNote : handleCreateNote}
          >
            {editingNote ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this note?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TechniqueNotes; 